import { Button, Typography, Box, useTheme } from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import DescriptionIcon from '@material-ui/icons/Description';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import StorageIcon from '@material-ui/icons/Storage';
import StorefrontIcon from '@material-ui/icons/Storefront';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import BuildIcon from '@material-ui/icons/Build';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import NotificationsIcon from '@material-ui/icons/Notifications';
import BallotIcon from '@material-ui/icons/Ballot';
import LinkIcon from '@material-ui/icons/Link';
import CloudIcon from '@material-ui/icons/Cloud';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import { useNavigate } from 'react-router-dom';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSignal } from '@backstage/plugin-signals-react';

const PulseKeyframes = () => createPortal(
  <style>{`
    @keyframes bp-pulse{0%,100%{opacity:1}50%{opacity:0.4}}
    @keyframes bp-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes bp-fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  `}</style>,
  document.head,
);

type MenuItem = {
  label: string;
  icon: React.ReactElement;
  configKey: string;
  to?: string;
  action?: string;
  color: string;
};

const ALL_MENU_ITEMS: MenuItem[] = [
  { label: 'AI Marketplace',  icon: <StorefrontIcon />, to: '/ai-marketplace',      configKey: 'marketplace', color: '#007FFF' },
  { label: 'Harness CI/CD',   icon: <AccountTreeIcon />, to: '/harness',            configKey: 'harness', color: '#00ade4' },
  { label: 'Quay Images',     icon: <StorageIcon />,     to: '/quay',               configKey: 'quay', color: '#ee0000' },
  { label: 'Datadog',         icon: <ShowChartIcon />,   to: '/datadog',            configKey: 'datadog', color: '#632ca6' },
  { label: 'Software Catalog',icon: <AppsIcon />,        to: '/catalog',            configKey: 'catalog', color: '#0ea5e9' },
  { label: 'Documentation',   icon: <DescriptionIcon />, to: '/documentation-menu', configKey: 'documentation', color: '#10b981' },
  { label: 'Submit Idea',     icon: <BallotIcon />,      to: '/ideas',              configKey: 'ideas', color: '#f59e0b' },
  { label: 'OKD Consoles',    icon: <CloudIcon />,       to: '/okd-consoles',       configKey: 'links', color: '#ef4444' },
  { label: 'Hydra Tools',     icon: <BuildIcon />,       to: '/our-tools-menu',     configKey: 'links', color: '#8b5cf6' },
  { label: 'Announcements',   icon: <AnnouncementIcon />, to: '/announcements',    configKey: 'announcements', color: '#ec4899' },
  { label: 'Tech Landscape', icon: <TrackChangesIcon />, to: '/tech-radar', configKey: 'techRadar', color: '#14b8a6' },
];

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high:     '#f59e0b',
  normal:   '#10b981',
  low:      '#94a3b8',
};

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high:     'High',
  normal:   'Success',
  low:      'Low',
};

const RecentNotifications = () => {
  const notificationsApi = useApi(notificationsApiRef);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const [notifications, setNotifications] = useState<any[]>([]);
  const { lastSignal } = useSignal('notifications');

  const fetchNotifications = useCallback(() => {
    notificationsApi
      .getNotifications({ limit: 10, offset: 0 })
      .then(resp => setNotifications((resp as any).notifications ?? []))
      .catch(() => {});
  }, [notificationsApi]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    if (lastSignal && (lastSignal as any).action) {
      fetchNotifications();
    }
  }, [lastSignal, fetchNotifications]);

  if (notifications.length === 0) return null;

  const formatRelativeDate = (date: string) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <Box style={{ marginTop: 48, paddingBottom: 32 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <NotificationsIcon style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', fontSize: 18 }} />
          <Typography style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', letterSpacing: 1.5, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
            Recent Activity
          </Typography>
        </Box>
        <Box
          onClick={() => navigate('/notifications')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <Typography style={{ color: '#6366f1', fontSize: 13, fontWeight: 600 }}>View all →</Typography>
        </Box>
      </Box>

      <Box
        style={{
          borderRadius: 18,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : '#ffffff',
          overflow: 'hidden',
        }}
      >
        {notifications.map((n: any, i: number) => {
          const severity: string = n.payload?.severity ?? 'normal';
          const dotColor = SEVERITY_COLOR[severity] ?? SEVERITY_COLOR.normal;
          const label = SEVERITY_LABEL[severity] ?? 'Normal';
          const isUnread = !n.read;
          const isLast = i === notifications.length - 1;
          return (
            <Box
              key={n.id}
              onClick={() => n.payload?.link && window.open(n.payload.link, '_blank', 'noopener,noreferrer')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '15px 22px',
                cursor: n.payload?.link ? 'pointer' : 'default',
                transition: 'background 0.18s ease',
                borderBottom: isLast ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                background: isUnread
                  ? (isDark ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.025)')
                  : 'transparent',
              }}
              onMouseEnter={e => { if (n.payload?.link) (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isUnread ? (isDark ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.025)') : 'transparent'; }}
            >
              {/* Status indicator */}
              <Box style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: dotColor,
                flexShrink: 0,
                boxShadow: `0 0 6px ${dotColor}40`,
              }} />

              {/* Content */}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{
                  color: isDark ? '#fff' : '#1a1a2e',
                  fontWeight: isUnread ? 600 : 450,
                  fontSize: 13.5,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {n.payload?.title}
                </Typography>
                {n.payload?.description && (
                  <Typography style={{
                    color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)',
                    fontSize: 12,
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {n.payload.description}
                  </Typography>
                )}
              </Box>

              {/* Status badge */}
              <Box style={{
                padding: '3px 8px',
                borderRadius: 6,
                background: `${dotColor}14`,
                flexShrink: 0,
              }}>
                <Typography style={{ fontSize: 11, fontWeight: 600, color: dotColor, letterSpacing: '0.01em' }}>
                  {label}
                </Typography>
              </Box>

              {/* Timestamp */}
              <Typography style={{
                color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.35)',
                fontSize: 11.5,
                flexShrink: 0,
                fontWeight: 500,
                minWidth: 60,
                textAlign: 'right',
              }}>
                {formatRelativeDate(n.created)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export const HomeMenu = () => {
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const sfr = configApi.getOptionalConfig('app.slowFeaturesReveal');
  const menuItems = ALL_MENU_ITEMS.filter(item =>
    sfr?.getOptionalBoolean(`${item.configKey}.enabled`) ?? false,
  );

  return (
    <Box style={{ padding: '40px 32px 0' }}>
      <PulseKeyframes />

      {/* Hero greeting */}
      <Box style={{ marginBottom: 40 }}>
        <Typography style={{ fontSize: 28, fontWeight: 800, color: isDark ? '#fff' : '#1a1a2e', letterSpacing: -0.5, lineHeight: 1.3 }}>
          Developer Portal
        </Typography>
        <Typography style={{ fontSize: 15, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)', marginTop: 6, lineHeight: 1.5 }}>
          Your unified gateway to platform services, tools, and resources.
        </Typography>
      </Box>

      {/* Shortcuts section */}
      <Box display="flex" alignItems="center" mb={2.5} style={{ gap: 10 }}>
        <LinkIcon style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', fontSize: 18 }} />
        <Typography style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', letterSpacing: 1.5, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
          Shortcuts
        </Typography>
      </Box>
      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, paddingBottom: 24, overflow: 'visible', position: 'relative', zIndex: 1 }}>
        {menuItems.map((item, idx) => (
          <Button
            key={item.label}
            variant="contained"
            onClick={() => item.action
              ? window.dispatchEvent(new CustomEvent(item.action))
              : navigate(item.to!)
            }
            style={{
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              minHeight: 100,
              boxShadow: 'none',
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
              border: `2px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              color: isDark ? 'rgba(255,255,255,0.85)' : '#1a1a2e',
              fontWeight: 600,
              fontSize: 12.5,
              textTransform: 'none',
              padding: '18px 12px',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: `bp-fadeIn 0.4s ease ${idx * 0.04}s both`,
              height: '100%',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${item.color}20`;
              e.currentTarget.style.borderColor = `${item.color}60`;
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 0 20px 6px ${item.color}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
              e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Box style={{ color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 5 }}>
              {item.icon}
            </Box>
            <Typography style={{ fontWeight: 600, fontSize: 13, textAlign: 'center', lineHeight: 1.3, color: isDark ? 'rgba(255,255,255,0.85)' : '#1a1a2e' }}>
              {item.label}
            </Typography>
          </Button>
        ))}
      </Box>

      <RecentNotifications />
    </Box>
  );
};
